import React, { useEffect, useRef, useState } from "react";
import axios from "axios";
import { Box, Typography, List, Avatar, CircularProgress } from "@mui/material";
import PhoneDisabledIcon from "@mui/icons-material/PhoneDisabled";
import { useAuth } from "../hooks/AuthProvider";
import { VOICE_CALLER_HISTORY_ENDPOINT } from "../config/urls";
import CallActivityCard from "./CallActivityCard";

const CALLS_PAGE_SIZE = 30;

const getInitials = (name) => {
  if (!name || name === "Anonymous") return "?";
  const parts = name.trim().split(" ");
  return parts.length >= 2
    ? `${parts[0][0]}${parts[1][0]}`.toUpperCase()
    : parts[0].slice(0, 2).toUpperCase();
};

// One caller's full call history — rendered by ContactDetailWindow.js's
// Calls sub-tab for whichever contact is selected in the unified ChatList
// (chat/calls/all). Ordering matches ChatWindow.js's own convention
// (oldest-first, newest at the bottom, scroll-up-for-older): the backend
// (CallerHistoryView) returns each page newest-first (offset-based
// pagination needs a stable order), and this component reverses each
// page before display and prepends older pages above — the same trick
// ChatWindow.js/CombinedActivityWindow.js use for their own paging.
const CallHistoryWindow = ({ caller, callerName }) => {
  const { getValidToken } = useAuth();
  const [calls, setCalls] = useState([]); // oldest -> newest, for display
  const [loading, setLoading] = useState(true);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(false);
  const [error, setError] = useState("");
  // Only one call recording plays at a time across this history — see
  // design.md Decision 1 of improve-call-recording-player. Each row is
  // told which call (if any) is the current "owner" of playback, and
  // pauses itself if it isn't the one that most recently started.
  const [playingCallId, setPlayingCallId] = useState(null);
  const containerRef = useRef(null);
  const prevScrollHeightRef = useRef(0);
  const prevCallCountRef = useRef(0);

  useEffect(() => {
    if (!caller) return;
    let cancelled = false;
    setLoading(true);
    setError("");
    setHasMore(false);
    prevCallCountRef.current = 0;

    (async () => {
      try {
        const token = await getValidToken();
        const tenant = localStorage.getItem("tenant");
        const response = await axios.get(VOICE_CALLER_HISTORY_ENDPOINT(caller), {
          headers: {
            Authorization: `Bearer ${token}`,
            "X-Tenant-ID": tenant,
          },
          params: { limit: CALLS_PAGE_SIZE, offset: 0 },
        });
        if (!cancelled) {
          // Newest-first off the wire -> oldest-first for display.
          setCalls([...(response.data.results || [])].reverse());
          setHasMore(Boolean(response.data.next));
        }
      } catch {
        if (!cancelled) setError("Could not load call history.");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [caller, getValidToken]);

  // Keep the view pinned to the bottom (newest) on first load and as new
  // calls arrive while already at the bottom.
  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;
    const wasAtBottom =
      container.scrollHeight - container.scrollTop - container.clientHeight < 100;
    if (wasAtBottom || prevCallCountRef.current === 0) {
      container.scrollTop = container.scrollHeight;
    }
    prevCallCountRef.current = calls.length;
  }, [calls]);

  // After an older page is prepended, restore the scroll position so the
  // content the user was looking at doesn't jump.
  useEffect(() => {
    const container = containerRef.current;
    if (!container || !prevScrollHeightRef.current) return;
    container.scrollTop = container.scrollHeight - prevScrollHeightRef.current;
    prevScrollHeightRef.current = 0;
  }, [calls]);

  const handleScroll = async () => {
    const el = containerRef.current;
    if (!el || !hasMore || isLoadingMore || loading) return;
    if (el.scrollTop > 0) return; // load older only when scrolled to the top

    setIsLoadingMore(true);
    prevScrollHeightRef.current = el.scrollHeight;
    try {
      const token = await getValidToken();
      const tenant = localStorage.getItem("tenant");
      const response = await axios.get(VOICE_CALLER_HISTORY_ENDPOINT(caller), {
        headers: {
          Authorization: `Bearer ${token}`,
          "X-Tenant-ID": tenant,
        },
        params: { limit: CALLS_PAGE_SIZE, offset: calls.length },
      });
      // An older page — reverse it too, then prepend above what's
      // already shown (older content grows upward).
      setCalls((prev) => [...[...(response.data.results || [])].reverse(), ...prev]);
      setHasMore(Boolean(response.data.next));
    } catch {
      // Leave hasMore as-is so the next scroll near the top retries.
    } finally {
      setIsLoadingMore(false);
    }
  };

  if (!caller) {
    return (
      <Box display="flex" flexDirection="column" alignItems="center" justifyContent="center" height="100%" px={2}>
        <PhoneDisabledIcon sx={{ fontSize: 48, color: "#ccc", mb: 1.5 }} />
        <Typography variant="body2" color="#888">
          Select a caller to see their call history
        </Typography>
      </Box>
    );
  }

  const displayName = callerName && callerName !== "Anonymous" ? callerName : `+${caller}`;

  return (
    <Box sx={{ display: "flex", flexDirection: "column", height: "100%", bgcolor: "#ece5dd" }}>
      {/* Header — same visual weight as ChatWindow's contact header */}
      <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, px: 2.5, py: 1.75, bgcolor: "#ffffff", borderBottom: "1px solid #e8e8e8" }}>
        <Avatar sx={{ width: 40, height: 40, bgcolor: "#1565c0", fontSize: "0.9rem", fontWeight: 700 }}>
          {getInitials(callerName)}
        </Avatar>
        <Box>
          <Typography sx={{ fontWeight: 700, fontSize: "0.95rem", color: "#222" }}>{displayName}</Typography>
          <Typography sx={{ fontSize: "0.78rem", color: "#888" }}>+{caller}</Typography>
        </Box>
      </Box>

      {loading ? (
        <Box display="flex" justifyContent="center" py={6}>
          <CircularProgress size={28} />
        </Box>
      ) : error ? (
        <Box px={2.5} py={2}>
          <Typography sx={{ fontSize: "0.85rem", color: "#c4515b" }}>{error}</Typography>
        </Box>
      ) : (
        <Box ref={containerRef} onScroll={handleScroll} sx={{ flex: 1, overflowY: "auto", px: 2.5, py: 2 }}>
          {isLoadingMore && (
            <Box display="flex" justifyContent="center" py={2}>
              <CircularProgress size={20} />
            </Box>
          )}
          <List disablePadding>
            {calls.map((call) => (
              <CallActivityCard
                key={call.id}
                call={call}
                activeCallId={playingCallId}
                onPlay={setPlayingCallId}
              />
            ))}
          </List>
          {calls.length === 0 && (
            <Typography sx={{ fontSize: "0.85rem", color: "#aaa", textAlign: "center", mt: 4 }}>
              No calls found for this caller.
            </Typography>
          )}
        </Box>
      )}
    </Box>
  );
};

export default CallHistoryWindow;
